import React from 'react';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const UAECustomsDutyGuide = () => {
  const guide = {
    ...getGuideBySlug('uae-customs-duty-guide'),
    relatedService: { label: 'Business Advisory', path: '/business-advisory-uae' }
  };

  const toc = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'what-is-customs', label: 'What is Customs Duty?' },
    { id: 'calculation', label: 'How is Customs Duty Calculated?' },
    { id: 'hs-code', label: 'What is an HS Code?' },
    { id: 'documentation', label: 'Customs Documentation' },
    { id: 'mainland-fz', label: 'Mainland vs Free Zone Imports' },
    { id: 'mistakes', label: 'Common Customs Mistakes' },
    { id: 'wpaa-help', label: 'How WPAA Can Help' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-customs" ctaService="customs">
      <Callout variant="info" title="A note on this guide">
        UAE customs rules are updated periodically by the Federal Customs Authority and local customs
        authorities. This guide covers the enduring fundamentals every importer needs to know. For anything
        specific to your shipments, please book a free consultation with our team.
      </Callout>

      <H2 id="introduction">Introduction</H2>
      <P>
        Customs duty is one of the oldest forms of taxation and applies to almost every business that imports
        goods into the UAE. Understanding how duty is calculated, how goods are classified and which
        documents are required is essential to avoid delays, penalties and back-duty assessments.
      </P>

      <H2 id="what-is-customs">What is Customs Duty?</H2>
      <P>
        Customs duty is a tax imposed on goods imported into the UAE. The standard rate is generally{' '}
        <strong>5%</strong>, although rates vary depending on product classification. Certain categories
        (such as tobacco and alcohol) attract higher rates, while others may be zero-rated.
      </P>

      <H2 id="calculation">How is Customs Duty Calculated?</H2>
      <P>Duty is generally calculated on the <strong>CIF value</strong>:</P>
      <BulletList items={['Cost of Goods', 'Insurance', 'Freight']} />
      <div className="my-6 rounded-xl border border-gray-200 bg-gray-50 p-5 text-center">
        <p className="text-sm text-gray-600 mb-1">Formula</p>
        <p className="text-lg font-bold text-gray-900">
          Customs Duty = CIF Value &times; Applicable Duty Rate
        </p>
      </div>

      <H2 id="hs-code">What is an HS Code?</H2>
      <P>The Harmonized System (HS) Code is an internationally standardised numbering system for classifying traded goods. HS Codes determine:</P>
      <BulletList items={['Applicable duty rates', 'Import restrictions or permits', 'Compliance requirements', 'Free-trade agreement eligibility']} />
      <Callout variant="warning" title="Correct classification matters">
        Incorrect HS classification can lead to penalties, shipment delays and back-duty assessments. Where
        classification is ambiguous, seek a professional opinion or a binding ruling from the relevant customs
        authority.
      </Callout>

      <H2 id="documentation">Customs Documentation</H2>
      <p className="text-gray-700 mb-2">Typical documents required for customs clearance in the UAE include:</p>
      <BulletList items={[
        'Commercial invoice',
        'Packing list',
        'Bill of lading or airway bill',
        'Certificate of origin',
        'Import permits (if applicable)',
        'Customs declaration'
      ]} />

      <H2 id="mainland-fz">Mainland vs Free Zone Imports</H2>
      <P>
        Goods entering UAE free zones may benefit from customs suspension while they remain inside the free
        zone. Duty generally becomes payable when goods are moved from the free zone into the UAE mainland
        market. Re-exports from free zones can, in many cases, be completed without triggering UAE customs
        duty.
      </P>
      <H3>Free Zone considerations</H3>
      <BulletList items={[
        'Goods stored in free zones are typically not subject to UAE customs duty until they enter the mainland',
        'Movement between free zones may require a customs transit declaration',
        'Records of stock, movement and re-exports must be kept for audit purposes'
      ]} />

      <H2 id="mistakes">Common Customs Mistakes</H2>
      <BulletList items={[
        'Using incorrect HS codes',
        'Under-declaring the value of goods',
        'Missing or incomplete documentation',
        'Improper certificates of origin',
        'Failing to reconcile customs declarations with accounting records'
      ]} />

      <H2 id="wpaa-help">How WPAA Can Help</H2>
      <P>Customs compliance protects businesses from penalties, shipment delays and reputational damage. It also unlocks legitimate savings through correct classification and use of free-trade agreements.</P>
      <P>WPAA supports UAE businesses with customs advisory, HS classification reviews, customs health checks and reconciliation of customs declarations with financial records.</P>
    </ArticleShell>
  );
};

export default UAECustomsDutyGuide;
